import React from 'react'
import { Box, Chip, Tooltip, Typography, useTheme } from '@mui/material'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import { useSearchParams } from 'react-router-dom'
import ROUTES from '../../../../consts/paths'
import { type TableCell } from '../../../../models/CompletionRate'
import { grey, shade, yellow, green } from '../../../../colors'

interface Instructor {
  id: string
  title: string
}

/**
 * Render a survey score as a colored Chip:
 * - greater than parent → green
 * - less than parent    → yellow
 * - equal to parent     → gray
 */
export const renderSurveyScore = (
  row: TableCell,
  parentSurveyScore: number | null | undefined
): JSX.Element => {
  const theme = useTheme()
  const score = row.surveyScore
  if (score == null || score === 0.0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: `0 ${theme.spacing(1)}`,
          width: '50px'

        }}
      >
        <Typography sx={{ color: grey[850] }}>--</Typography>
      </Box>
    )
  }

  const parent = parentSurveyScore ?? 0

  let backgroundColor: string
  let borderColor: string
  let textColor: string

  const difference = score - parent

  switch (true) {
    case difference > 0:
      // bigger than → green
      backgroundColor = green[0]
      borderColor = green[200]
      textColor = green[200]
      break
    case difference < 0:
      // smaller than → yellow
      backgroundColor = yellow[100]
      borderColor = yellow[400]
      textColor = yellow[500]
      break
    default:
      // exactly equal → gray
      backgroundColor = shade[300]
      borderColor = shade[500]
      textColor = grey[500]
      break
  }

  return (
    <Chip
      label={score.toFixed(2)}
      variant="filled"
      size="small"
      sx={{
        fontWeight: 'bold',
        border: 'none',
        backgroundColor,
        color: textColor,
        borderRadius: theme.spacing(1.5),
        padding: `0 ${theme.spacing(1)}`,
        height: theme.spacing(6),
        lineHeight: '1'
      }}
    />
  )
}

export const renderInstructorDetails = (row: TableCell): JSX.Element => {
  const [searchParams] = useSearchParams()
  const termId = searchParams.get('termId')
  const seriesId = searchParams.get('seriesId')

  const instructors = row.instructorDetails ?? []
  if (instructors.length === 0) {
    return <Typography>--</Typography>
  }

  const renderLink = (inst: Instructor): any => {
    const url = `${ROUTES.INSTRUCTOR_SURVEYS}/${inst.id}?termId=${termId}&seriesId=${seriesId}`
    return (
      <Box
        key={inst.id}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          window.open(url, '_blank')
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          textDecoration: 'underline',
          color: 'primary.main',
          cursor: 'pointer',
          mb: 0.5,
          lineHeight: 1.5
        }}
      >
        <Typography>{inst.title}</Typography>
        <ArrowOutwardIcon sx={{ fontSize: 16 }} />
      </Box>
    )
  }

  const [first, ...rest] = instructors
  const theme = useTheme()

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {renderLink(first)}
      {rest.length > 0 && (
        <Tooltip
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: shade[200],
                border: `1px solid ${grey[700]}`,
                p: theme.spacing(2),
                maxWidth: 300
              }
            }
          }}
          title={<Box>{rest.map(renderLink)}</Box>}
          placement="top"
          arrow
        >
          <Typography sx={{ cursor: 'pointer', color: 'primary.main' }}>
            +{rest.length}
          </Typography>
        </Tooltip>
      )}
    </Box>
  )
}
